/**
 * Connection lifecycle event handlers — connect, disconnect, connect_error,
 * sessionTakenOver, auth refresh.
 */

import type { Socket } from "socket.io-client";
import {
  type ServerToClientEvents,
  type ClientToServerEvents,
  VpcErrorCode,
  SettingsErrorCode,
  SubscriptionErrorCode,
} from "@amzn/a4k-creator-studio-shared";
import { useStoryStore } from "../storyStore";
import { useComicStore } from "../comicStore";
import { useChatStore } from "../chatStore";
import { useConfigStore } from "../configStore";
import { closeAll as closeAllDbs } from "../../lib/storage";
import { useSocketStore } from "../socketStore";
import { useErrorStore } from "../errorStore";
import { useNetworkStore, isDeviceOnline, cancelSocketDown } from "../networkStore";
import { getCachedImages } from "../../lib/imageCache";
import { loadCachedFullState } from "./fullStateHandlers";
import minervaService from "../../services/minervaService";
import { trackConnection, trackError, trackErrorToast, trackSession, getAppSessionId, setOnSessionRotate, Connection, Session } from "../../services/metrics";
import { AppEvent } from "../../services/appEvents";
import { GENERIC_ERROR } from "../errorMessages";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const ACCESS_RETRY_INTERVAL_MS = 30_000;
const ACCESS_RETRY_MAX_ATTEMPTS = 10;
let accessRetryTimer: ReturnType<typeof setInterval> | null = null;
let accessRetryCount = 0;

function clearAccessRetryTimer(): void {
  if (accessRetryTimer) {
    clearInterval(accessRetryTimer);
    accessRetryTimer = null;
  }
}

export function stopAccessRetry(resetCount = true): void {
  clearAccessRetryTimer();
  if (resetCount) accessRetryCount = 0;
}

function startAccessRetry(): void {
  if (accessRetryCount >= ACCESS_RETRY_MAX_ATTEMPTS) {
    console.warn("[accessRetry] Max attempts reached, not restarting");
    return;
  }
  clearAccessRetryTimer();
  accessRetryTimer = setInterval(() => {
    if (accessRetryCount >= ACCESS_RETRY_MAX_ATTEMPTS) {
      console.warn("[accessRetry] Max attempts reached, stopping");
      clearAccessRetryTimer();
      return;
    }
    const { socket, initSocket } = useSocketStore.getState();
    if (socket) return;
    accessRetryCount++;
    initSocket().catch((err) =>
      console.error("[accessRetry] Retry failed:", err)
    );
  }, ACCESS_RETRY_INTERVAL_MS);
}

let sessionStartMs = 0;
let sessionStartedForAppSession: string | null = null;

setOnSessionRotate(() => {
  if (sessionStartMs > 0) {
    trackSession(Session.End, Date.now() - sessionStartMs);
    sessionStartMs = 0;
    sessionStartedForAppSession = null;
  }
});

export function resetSessionStartMs(): void {
  sessionStartMs = 0;
  sessionStartedForAppSession = null;
}

export function endSessionIfActive(): void {
  if (sessionStartMs > 0) {
    trackSession(Session.End, Date.now() - sessionStartMs);
  }
  resetSessionStartMs();
}

/** Load cached fullState from IndexedDB and apply to stores with cached images.
 *  Aborts at each async boundary if the socket reconnected (isConnected),
 *  preventing stale cached data from overwriting fresh server state. */
async function loadCachedOfflineState(): Promise<void> {
  try {
    const data = await loadCachedFullState();
    if (!data) return;

    // Socket reconnected while we were reading IndexedDB — server will push fresh fullState
    if (useSocketStore.getState().isConnected) return;

    if (data.story) useStoryStore.getState().replaceAll(data.story);
    if (data.comic) useComicStore.getState().replaceAll(data.comic);
    if (data.config) useConfigStore.getState().replaceAll(data.config);
    // gallery not restored here — fetchComics() has its own offline fallback via loadCachedGallery()

    // Rehydrate images from IndexedDB cache
    const imageIds: string[] = [];
    for (const c of data.story?.characters || []) if (c.imageId) imageIds.push(c.imageId);
    for (const s of data.story?.scenes || []) if (s.imageId) imageIds.push(s.imageId);
    for (const b of data.story?.beats || []) if (b.imageId) imageIds.push(b.imageId);
    for (const p of data.comic?.pages || []) if (p.imageId) imageIds.push(p.imageId);

    if (imageIds.length > 0) {
      const cached = await getCachedImages(imageIds);

      // Re-check after second async gap
      if (useSocketStore.getState().isConnected) return;

      const storyState = useStoryStore.getState();
      for (const c of storyState.characters) {
        if (c.imageId && cached.has(c.imageId)) storyState.updateCharacter(c.characterId, { imageBase64: cached.get(c.imageId) });
      }
      for (const s of storyState.scenes) {
        if (s.imageId && cached.has(s.imageId)) storyState.updateScene(s.sceneId, { imageBase64: cached.get(s.imageId) });
      }
      for (const b of storyState.beats) {
        if (b.imageId && cached.has(b.imageId)) storyState.updateBeat(b.beatId, { imageBase64: cached.get(b.imageId) });
      }
      const comicState = useComicStore.getState();
      for (const p of comicState.pages) {
        if (p.imageId && cached.has(p.imageId)) comicState.setPageImage(p.pageNumber, cached.get(p.imageId)!);
      }
      console.log(`[offline] Restored ${cached.size}/${imageIds.length} images from cache`);
    }
  } catch (err) {
    console.error("[offline] Failed to load cached state:", err);
  }
}

export function registerConnectionHandlers(
  socket: TypedSocket,
  set: (partial: Partial<{
    isConnected: boolean;
    sessionTakenOver: boolean;
    isVoiceActive: boolean;
    socket: TypedSocket | null;
    fullStateReceived: boolean;
    accessBlocked: string | null;
  }>) => void,
): void {
  let connectErrorCount = 0;

  socket.on("connect", () => {
    console.log("[socketStore] Connected — waiting for fullState from server");
    connectErrorCount = 0;
    stopAccessRetry(false);
    const currentAppSession = getAppSessionId();
    const isReconnect = sessionStartedForAppSession === currentAppSession;
    if (!isReconnect) {
      sessionStartMs = Date.now();
      sessionStartedForAppSession = currentAppSession;
    }
    set({ isConnected: true });
    useNetworkStore.getState().setOffline(false);
    minervaService.recordAppEvent(AppEvent.SOCKET_CONNECT);
    trackConnection(Connection.SocketConnect, isReconnect ? "reconnect" : "success");
    if (!isReconnect) trackSession(Session.Start);
    // Voice resume is driven by the fullState handler, not here: on connect the
    // server hasn't yet re-established the session, so a sessionStart now races
    // ahead of it and the stream never wires up. fullState is the readiness
    // signal (pushed on every reconnect) and calls resumeVoice() there.
  });

  socket.on("disconnect", () => {
    console.log("[socketStore] Disconnected");
    set({ isConnected: false });
    useSocketStore.getState().suspendVoice();
    useNetworkStore.getState().reportSocketDown();
    trackConnection(Connection.SocketDisconnect);
  });

  socket.on("connect_error", async (err) => {
    console.error("[socketStore] Connection error:", err.message);
    trackError("Connection", "connect_error");
    connectErrorCount++;
    useNetworkStore.getState().reportSocketDown();
    // If fullState was already received (reconnecting after a drop), the user
    // already has content on screen — don't touch it.
    if (useSocketStore.getState().fullStateReceived) return;

    const deviceOffline = !(await isDeviceOnline());
    // Re-check after the await: a connect+fullState may have landed meanwhile.
    const s = useSocketStore.getState();
    if (s.isConnected || s.fullStateReceived) return;
    if (deviceOffline || connectErrorCount >= 3) {
      set({ isConnected: false, fullStateReceived: true });
      useNetworkStore.getState().setOffline(true, deviceOffline ? "device" : "server");
      loadCachedOfflineState();
    }
  });

  // Server warns that auth is about to expire — send a token
  socket.on("authExpiry", (_data) => {
    useSocketStore.getState().refreshAuth();
  });

  socket.on("authRefreshed", () => {
    console.log("[socketStore] Auth refreshed successfully");
  });

  socket.on("authError", (data) => {
    console.error("[socketStore] Auth error:", data.code, data.message);
    socket.io.opts.reconnection = false;
    minervaService.recordAppEvent(AppEvent.AUTH_ERROR);
    trackConnection(Connection.AuthError, data.code);
  });

  const handleAccessBlocked = (data: { code: string }, autoRetry: boolean) => {
    useSocketStore.getState().stopVoice();
    socket.io.opts.reconnection = false;
    socket.disconnect();
    cancelSocketDown();
    set({ socket: null, isConnected: false, accessBlocked: data.code });
    if (autoRetry) startAccessRetry();
  };

  const handleCheckFailed = () => {
    stopAccessRetry();
    useSocketStore.getState().stopVoice();
    socket.io.opts.reconnection = false;
    socket.disconnect();
    cancelSocketDown();
    set({ socket: null, isConnected: false });
    trackErrorToast("Connection", "access_check_failed");
    useErrorStore.getState().addError(GENERIC_ERROR);
  };

  socket.on("vpcError", (data) => {
    minervaService.recordAppEvent(AppEvent.VPC_ERROR);
    trackConnection(Connection.VPCError);
    if (data.code === VpcErrorCode.NOT_GRANTED) {
      handleAccessBlocked(data, true);
    } else if (data.code === VpcErrorCode.CHECK_FAILED) {
      handleCheckFailed();
    }
  });

  socket.on("settingsError", (data) => {
    minervaService.recordAppEvent(AppEvent.SETTINGS_ERROR);
    trackConnection(Connection.SettingsError);
    if (data.code === SettingsErrorCode.DISABLED) {
      handleAccessBlocked(data, true);
    } else if (data.code === SettingsErrorCode.CHECK_FAILED) {
      handleCheckFailed();
    }
  });

  socket.on("subscriptionError", (data) => {
    console.error("[socketStore] Subscription error:", data.code, data.message);
    minervaService.recordAppEvent(AppEvent.SUBSCRIPTION_ERROR);
    trackConnection(Connection.SubscriptionError);
    if (data.code === SubscriptionErrorCode.NOT_SUBSCRIBED) {
      handleAccessBlocked(data, false);
    } else {
      handleCheckFailed();
    }
  });

  // All reconnection attempts exhausted — stop retrying.
  socket.io.on("reconnect_failed", () => {
    console.error("[socketStore] All reconnection attempts exhausted");
    minervaService.recordAppEvent(AppEvent.RECONNECT_FAILED);
    useSocketStore.getState().stopVoice();
    set({ socket: null, isConnected: false });
    useNetworkStore.getState().setOffline(true, "server");
    if (!useSocketStore.getState().fullStateReceived) {
      set({ fullStateReceived: true });
      loadCachedOfflineState();
    }
  });

  // Another tab/instance connected with the same session — surrender
  socket.on("sessionTakenOver", () => {
    console.warn("[socketStore] Session taken over by another instance");
    endSessionIfActive();
    trackConnection(Connection.SessionTakenOver);
    // Stop voice if active
    useSocketStore.getState().stopVoice();
    // Clear all stores
    useStoryStore.getState().clearAll();
    useComicStore.getState().clearAll();
    useChatStore.getState().clearMessages();
    // Release IndexedDB connections so the new tab is not blocked
    closeAllDbs();
    // Prevent reconnection and mark as taken over
    socket.disconnect();
    cancelSocketDown();
    set({ socket: null, isConnected: false, sessionTakenOver: true });
  });
}
